import { z } from '@customerio/hydra-validation';
import { isEmptyString } from '@/utils/string';

const OAUTH_REQUEST_KEY = '__oauth_request';

// Message type posted by the SAML ACS endpoint back to the opener window when
// an add_session login completes (see services `ui_api/auth/login_saml.go`).
const SAML_SESSION_ADDED_MESSAGE_TYPE = 'saml_session_added';

/**
 * Payload the SAML ACS endpoint posts back on an add_session login. The
 * `sessions` array mirrors `SessionMetadata` from `@customerio/hydra-session-state`;
 * it's declared inline (as the Ember original was) so this module stays out of
 * the session-state import chain, which reads `localStorage` at load time.
 */
export const AddSessionResponse = z.object({
  session_index: z.number(),
  sessions: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      email: z.string(),
      expires_at: z.number(),
      workspace_ids: z.array(z.number()),
    }),
  ),
});
export type AddSessionResponse = z.infer<typeof AddSessionResponse>;

export class SAMLAuthError extends Error {
  constructor(message: string) {
    super(message);

    this.name = 'SAMLAuthError';
  }
}

export type SAMLPopupOptions = {
  /** The SAML IdP issuer URL (already carries its own query string). */
  authorizationURL: string;
  email?: string;
  width?: number;
  height?: number;
};

/**
 * Open the SAML IdP popup and resolve once it closes.
 *
 * Unlike the OIDC popup (which reads an authorization code from `localStorage`),
 * SAML logins complete server-side; for an add_session login the ACS endpoint
 * posts the new session metadata back via `postMessage`. We validate that
 * message at the boundary — same-origin and from our own popup — and return the
 * parsed payload. For a normal (non-add_session) SAML login no message is sent
 * and the popup just closes, so the resolved value is `undefined`.
 */
export async function openSAMLPopup({
  authorizationURL,
  email,
  width = 500,
  height = 500,
}: SAMLPopupOptions): Promise<AddSessionResponse | undefined> {
  localStorage.setItem(OAUTH_REQUEST_KEY, window.crypto.randomUUID());

  const url =
    email != null && !isEmptyString(email)
      ? `${authorizationURL}${authorizationURL.includes('?') ? '&' : '?'}email=${encodeURIComponent(email)}`
      : authorizationURL;

  const popup = window.open(
    url,
    '_blank',
    `width=${width},height=${height},left=${screen.width / 2 - width / 2},top=${screen.height / 2 - height / 2}`,
  );

  if (popup == null) {
    throw new SAMLAuthError('Unable to open SAML Auth window');
  }

  let payload: AddSessionResponse | undefined;
  const messageHandler = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      return;
    }
    if (event.source !== popup) {
      return;
    }

    const data = event.data as { type?: unknown; payload?: unknown } | null | undefined;
    if (data?.type !== SAML_SESSION_ADDED_MESSAGE_TYPE) {
      return;
    }

    const parsed = AddSessionResponse.safeParse(data.payload);
    if (parsed.success) {
      payload = parsed.data;
    }
  };
  window.addEventListener('message', messageHandler);

  try {
    // Poll for closure: `beforeunload` only fires same-origin, and the popup
    // navigates to the IdP (cross-origin) before returning.
    while (!popup.closed) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  } finally {
    window.removeEventListener('message', messageHandler);
  }

  return payload;
}
